/**
 * Service pour la gestion des paiements et intégration avec les cartes
 * Utilise les nouvelles APIs d'intégration paiement du backend MS_Client
 */

const API_BASE_URL = process.env.REACT_APP_BASEURL_CLIENT_BACKEND;

// Configuration des headers
const getHeaders = () => {
    // TEMPORAIREMENT SUSPENDU : l'authentification Bearer token
    // const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    return {
        'Content-Type': 'application/json'
        // 'Authorization': userDetails?.token ? `Bearer ${userDetails.token}` : ''
    };
};

class PaymentService {

    // ===== INFORMATION ET ÉLIGIBILITÉ PAIEMENT =====

    /**
     * Obtenir les informations de paiement d'une carte
     */
    async getCardPaymentInfo(cardId) {
        if (!cardId || isNaN(cardId)) {
            throw new Error('ID de carte invalide');
        }

        const response = await fetch(`${API_BASE_URL}/Card/${cardId}/payment-info`, {
            method: 'GET',
            headers: getHeaders()
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors de la récupération des informations de paiement: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Vérifier l'éligibilité d'une carte au paiement
     */
    async checkPaymentEligibility(cardId) {
        if (!cardId || isNaN(cardId)) {
            throw new Error('ID de carte invalide');
        }

        const response = await fetch(`${API_BASE_URL}/Card/${cardId}/payment-eligibility`, {
            method: 'GET',
            headers: getHeaders()
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors de la vérification d'éligibilité: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Calculer le coût de production d'une carte
     */
    async calculateCardCost(cardId, countryCode) {
        if (!cardId || isNaN(cardId)) {
            throw new Error('ID de carte invalide');
        }

        if (!countryCode) {
            throw new Error('Code pays requis');
        }

        const response = await fetch(`${API_BASE_URL}/Card/${cardId}/calculate-cost`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ countryCode })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors du calcul du coût: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Obtenir le statut complet paiement et production
     */
    async getPaymentProductionStatus(cardId) {
        if (!cardId || isNaN(cardId)) {
            throw new Error('ID de carte invalide');
        }

        const response = await fetch(`${API_BASE_URL}/Card/${cardId}/payment-production-status`, {
            method: 'GET',
            headers: getHeaders()
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors de la récupération du statut: ${response.status}`);
        }

        return await response.json();
    }

    // ===== TRAITEMENT DES PAIEMENTS =====

    /**
     * Traiter le paiement d'une carte
     */
    async processCardPayment(cardId, paymentData) {
        if (!cardId || isNaN(cardId)) {
            throw new Error('ID de carte invalide');
        }

        const paymentRequest = {
            cardId: parseInt(cardId),
            provider: paymentData.provider,
            amount: paymentData.amount || 0,
            currency: paymentData.currency || 'XOF',
            countryCode: paymentData.countryCode,
            phoneNumber: paymentData.phoneNumber,
            clientName: paymentData.clientName,
            clientEmail: paymentData.clientEmail
        };

        const response = await fetch(`${API_BASE_URL}/Card/${cardId}/process-payment`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(paymentRequest)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors du traitement du paiement: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Marquer une carte comme payée (pour les paiements manuels)
     */
    async markCardAsPaid(cardId, transactionId, provider) {
        if (!cardId || isNaN(cardId)) {
            throw new Error('ID de carte invalide');
        }

        if (!transactionId) {
            throw new Error('ID de transaction requis');
        }

        const response = await fetch(`${API_BASE_URL}/Card/${cardId}/mark-as-paid`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({
                transactionId,
                provider: provider || 'BankTransfer'
            })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors du marquage de la carte: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Initier la production après paiement confirmé
     */
    async initiateProductionAfterPayment(cardId, transactionId) {
        if (!cardId || isNaN(cardId)) {
            throw new Error('ID de carte invalide');
        }

        if (!transactionId) {
            throw new Error('ID de transaction requis');
        }

        const response = await fetch(`${API_BASE_URL}/Card/${cardId}/initiate-production-after-payment`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ transactionId })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors du lancement de la production: ${response.status}`);
        }

        return await response.json();
    }

    // ===== APIs DE PAIEMENT GÉNÉRIQUES =====

    /**
     * Obtenir les méthodes de paiement disponibles pour un pays
     */
    async getPaymentMethods(countryCode) {
        if (!countryCode) {
            throw new Error('Code pays requis');
        }

        const response = await fetch(`${API_BASE_URL}/api/Payment/methods/${countryCode}`, {
            method: 'GET',
            headers: getHeaders()
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors de la récupération des méthodes de paiement: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Vérifier le statut d'un paiement
     */
    async getPaymentStatus(transactionId) {
        if (!transactionId) {
            throw new Error('ID de transaction requis');
        }

        const response = await fetch(`${API_BASE_URL}/api/Payment/status/${transactionId}`, {
            method: 'GET',
            headers: getHeaders()
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors de la récupération du statut: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Obtenir l'historique des paiements pour une carte
     */
    async getPaymentHistory(cardId) {
        if (!cardId || isNaN(cardId)) {
            throw new Error('ID de carte invalide');
        }

        const response = await fetch(`${API_BASE_URL}/api/Payment/history/${cardId}`, {
            method: 'GET',
            headers: getHeaders()
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors de la récupération de l'historique: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Annuler un paiement
     */
    async cancelPayment(transactionId) {
        if (!transactionId) {
            throw new Error('ID de transaction requis');
        }

        const response = await fetch(`${API_BASE_URL}/api/Payment/cancel/${transactionId}`, {
            method: 'POST',
            headers: getHeaders()
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors de l'annulation: ${response.status}`);
        }

        return await response.json();
    }

    // ===== PAIEMENTS GROUPÉS =====

    /**
     * Obtenir les cartes disponibles pour paiement groupé
     */
    async getSelectableCards(filters = {}) {
        const response = await fetch(`${API_BASE_URL}/api/Payment/selectable-cards`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(filters)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors de la récupération des cartes: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Calculer le coût d'un paiement groupé
     */
    async calculateBulkPaymentCost(cardIds, countryCode) {
        if (!Array.isArray(cardIds) || cardIds.length === 0) {
            throw new Error('Liste des cartes requise');
        }

        if (!countryCode) {
            throw new Error('Code pays requis');
        }

        const response = await fetch(`${API_BASE_URL}/api/Payment/calculate-cost`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ cardIds, countryCode })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors du calcul du coût: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Traiter un paiement groupé
     */
    async processBulkPayment(bulkPaymentData) {
        if (!Array.isArray(bulkPaymentData.cardIds) || bulkPaymentData.cardIds.length === 0) {
            throw new Error('Liste des cartes requise');
        }

        const response = await fetch(`${API_BASE_URL}/api/Payment/bulk-process`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(bulkPaymentData)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors du traitement du paiement groupé: ${response.status}`);
        }

        return await response.json();
    }

    // ===== RAPPORTS ET STATISTIQUES =====

    /**
     * Obtenir l'historique des paiements avec filtres
     */
    async getPaymentHistory(params = {}) {
        const queryString = new URLSearchParams({
            page: params.page || 1,
            pageSize: params.pageSize || 20,
            ...params
        }).toString();

        const response = await fetch(`${API_BASE_URL}/api/Payment/history?${queryString}`, {
            method: 'GET',
            headers: getHeaders()
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors de la récupération de l'historique: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Obtenir un résumé des paiements
     */
    async getPaymentSummary(params = {}) {
        const response = await fetch(`${API_BASE_URL}/api/Payment/summary`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors de la récupération du résumé: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Obtenir les paiements par période
     */
    async getPaymentsByPeriod(params = {}) {
        const response = await fetch(`${API_BASE_URL}/api/Payment/reports/by-period`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors de la récupération des données par période: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Obtenir les paiements par fournisseur
     */
    async getPaymentsByProvider(params = {}) {
        const response = await fetch(`${API_BASE_URL}/api/Payment/reports/by-provider`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors de la récupération des données par fournisseur: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Obtenir les paiements par statut
     */
    async getPaymentsByStatus(params = {}) {
        const response = await fetch(`${API_BASE_URL}/api/Payment/reports/by-status`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors de la récupération des données par statut: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Obtenir le top des cartes par montant de paiement
     */
    async getTopPaymentCards(params = {}) {
        const response = await fetch(`${API_BASE_URL}/api/Payment/reports/top-cards`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ ...params, limit: params.limit || 10 })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || `Erreur lors de la récupération du top des cartes: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Vérifier l'éligibilité de paiement d'une carte (méthode d'alias)
     */
    async checkCardPaymentEligibility(cardId) {
        return this.checkPaymentEligibility(cardId);
    }

    /**
     * Traiter un paiement de carte (méthode simplifiée)
     */
    async processCardPaymentSimple(paymentData) {
        if (!paymentData.cardId) {
            throw new Error('ID de carte requis');
        }

        return this.processCardPayment(paymentData.cardId, paymentData);
    }

    // ===== UTILITAIRES =====

    /**
     * Formater un montant avec la devise
     */
    formatCurrency(amount, currency = 'XOF') {
        const options = {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        };

        switch (currency) {
            case 'XOF':
                return new Intl.NumberFormat('fr-FR', options).format(amount);
            case 'XAF':
                return new Intl.NumberFormat('fr-CM', options).format(amount);
            case 'EUR':
                return new Intl.NumberFormat('fr-FR', options).format(amount);
            default:
                return new Intl.NumberFormat('en-US', options).format(amount);
        }
    }

    /**
     * Obtenir le libellé d'un statut de paiement
     */
    getPaymentStatusLabel(status) {
        const statusLabels = {
            'Pending': 'En attente',
            'Processing': 'En cours',
            'Completed': 'Terminé',
            'Failed': 'Échec',
            'Cancelled': 'Annulé',
            'Refunded': 'Remboursé'
        };

        return statusLabels[status] || status;
    }

    /**
     * Obtenir la classe CSS pour un statut de paiement
     */
    getPaymentStatusClass(status) {
        const statusClasses = {
            'Pending': 'warning',
            'Processing': 'info',
            'Completed': 'success',
            'Failed': 'danger',
            'Cancelled': 'secondary',
            'Refunded': 'info'
        };

        return statusClasses[status] || 'secondary';
    }

    /**
     * Obtenir le nom d'affichage d'un fournisseur de paiement
     */
    getPaymentProviderName(provider) {
        const providerNames = {
            'OrangeMoney': 'Orange Money',
            'MtnMoney': 'MTN Money',
            'BankTransfer': 'Virement Bancaire',
            'Cash': 'Espèces'
        };

        return providerNames[provider] || provider;
    }
}

// Instance unique du service
export const paymentService = new PaymentService();
export default PaymentService;